import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Tent, FirstAid, ChatCircleText, Heartbeat, Bed, ArrowCounterClockwise, Warning, ShieldPlus,
} from "@phosphor-icons/react";
import api, { apiErr } from "../api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import CrewProgression from "../components/CrewProgression";

const HEALTH_COLOR = {
  Healthy: "text-ff-success",
  Injured: "text-ff-danger",
  Recovering: "text-ff-brass",
  Resting: "text-ff-brass",
  Shaken: "text-amber-500",
};

function fmtEta(hours) {
  if (hours == null) return "";
  if (hours < 1) return "under an hour";
  if (hours < 2) return "an hour";
  if (hours < 36) return `${Math.round(hours)} hours`;
  const d = Math.round(hours / 24);
  return `${d} days`;
}

export default function RecoveryCamp() {
  const [data, setData] = useState(null);
  const [meeting, setMeeting] = useState(null); // {crew_id, dialogue}
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/camp").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const act = async (path, crew_id, okMsg) => {
    setBusy(true);
    try {
      await api.post(path, { crew_id });
      toast.success(okMsg);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const openMeeting = async (crew_id) => {
    try {
      const { data: dlg } = await api.get(`/camp/meeting/${crew_id}`);
      setMeeting({ crew_id, dialogue: dlg });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const chooseMeeting = async (option_id) => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/camp/meeting", { crew_id: meeting.crew_id, option_id });
      toast.success(`Loyalty ${res.loyalty_delta >= 0 ? "+" : ""}${res.loyalty_delta}`);
      setMeeting(null);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="py-20 text-center text-ff-muted">Setting up camp...</div>;

  return (
    <div data-testid="recovery-camp">
      <div className="mb-1 flex items-center gap-2">
        <Tent size={26} weight="duotone" className="text-ff-gold" />
        <h1 className="font-display text-4xl font-bold tracking-tight">Recovery Camp</h1>
      </div>
      <p className="mb-5 font-display text-lg italic text-ff-muted">"Every expedition leaves a mark."</p>

      <div className="mb-5 flex items-center gap-3 rounded-md border border-ff-brass/30 bg-ff-bg2/60 px-4 py-3">
        <FirstAid size={22} weight="duotone" className="text-ff-danger" />
        <div>
          <div className="text-xs text-ff-muted">Medical Supplies (earned, non-purchasable)</div>
          <div className="font-mono-ff text-xl font-bold text-ff-gold" data-testid="medical-supplies-count">{data.medical_supplies}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.crew.map((c, i) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="ff-panel rounded-md p-5"
            data-testid={`camp-crew-${c.id}`}
          >
            <div className="flex items-center gap-3">
              <img src={c.portrait} alt={c.name} className="h-14 w-14 rounded-sm object-cover" loading="lazy" />
              <div className="min-w-0">
                <div className="font-display text-lg font-semibold leading-tight text-ff-parchment">{c.name}</div>
                <div className={`text-xs font-medium ${HEALTH_COLOR[c.health] || "text-ff-muted"}`}>
                  {c.injury_status ? `Injured · ${c.injury_severity}` : c.health}
                  {c.recovery_remaining > 0 && ` · ${c.recovery_remaining} left`}
                </div>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
              <MiniMeter label="Loyalty" value={c.loyalty} good />
              <MiniMeter label="Stress" value={c.stress} />
            </div>
            <div className="mt-2 text-xs text-ff-muted">Level {c.level} · {c.available ? <span className="text-ff-success">Available</span> : <span className="text-ff-danger">Unavailable</span>}</div>
            <CrewProgression id={c.id} progression={c.progression} />
            {!c.available && c.unavailable_reason && (
              <div className="mt-2 rounded-sm bg-ff-danger/10 px-2 py-1.5 text-[11px] text-red-300" data-testid={`recovery-info-${c.id}`}>
                <div className="flex items-start gap-1">
                  <Warning size={12} className="mt-0.5 shrink-0" />
                  <span className="font-medium">{c.recovery_reason || c.unavailable_reason}</span>
                </div>
                {c.recovery_eta_hours != null && (
                  <div className="mt-1 pl-4 text-ff-muted" data-testid={`recovery-eta-${c.id}`}>
                    Ready in about {fmtEta(c.recovery_eta_hours)}
                    {c.treatment_can_shorten && " · Medical care in Camp can shorten this"}
                  </div>
                )}
              </div>
            )}

            <div className="mt-3 grid grid-cols-2 gap-2">
              <CampBtn onClick={() => act("/camp/rest", c.id, `${c.name} is resting`)} disabled={busy || c.recovery_remaining > 0} testid={`rest-${c.id}`} icon={Bed}>Rest</CampBtn>
              <CampBtn onClick={() => act("/camp/medical", c.id, "Medical care applied")} disabled={busy || (!c.injury_status && !["Recovering","Resting","Shaken"].includes(c.health)) || data.medical_supplies < 1} testid={`medical-${c.id}`} icon={ShieldPlus}>Medical</CampBtn>
              <CampBtn onClick={() => openMeeting(c.id)} disabled={busy || !c.meeting_available} testid={`meeting-${c.id}`} icon={ChatCircleText}>Meeting</CampBtn>
              <CampBtn onClick={() => act("/camp/return-to-duty", c.id, `${c.name} returned to duty`)} disabled={busy || c.recovery_remaining > 0 || c.injury_status} testid={`return-${c.id}`} icon={ArrowCounterClockwise}>Return</CampBtn>
            </div>
          </motion.div>
        ))}
      </div>

      {data.recent_effects?.length > 0 && (
        <div className="ff-panel mt-6 rounded-md p-5">
          <h2 className="mb-3 flex items-center gap-2 font-display text-xl font-semibold"><Heartbeat size={20} className="text-ff-gold" /> Recent Expedition Effects</h2>
          <div className="space-y-1.5 text-xs" data-testid="camp-recent-effects">
            {data.recent_effects.slice(0, 8).map((e) => (
              <div key={e.id} className="flex items-center justify-between rounded-sm bg-ff-bg/40 px-3 py-2">
                <span className="text-ff-parchment">{e.crew_id} · {e.outcome.replace("_", " ")}</span>
                <span className="font-mono-ff text-ff-muted">
                  XP +{e.xp_gain} · stress {e.stress_delta >= 0 ? "+" : ""}{e.stress_delta}{e.injured ? ` · injured (${e.severity})` : ""}{e.leveled_up ? ` · Lv ${e.new_level}!` : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!meeting} onOpenChange={(o) => !o && setMeeting(null)}>
        <DialogContent className="border-ff-brass/40 bg-ff-bg2 text-ff-parchment" data-testid="meeting-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl text-ff-gold">Personal Meeting</DialogTitle></DialogHeader>
          {meeting && (
            <div>
              {meeting.dialogue.renown_remark && (
                <p className="mb-3 rounded-sm border border-ff-brass/25 bg-ff-bg/40 p-2.5 text-sm text-ff-brass" data-testid="meeting-renown-remark">
                  {meeting.dialogue.renown_remark}
                </p>
              )}
              <p className="mb-4 text-sm italic text-ff-muted">{meeting.dialogue.prompt}</p>
              <div className="space-y-2">
                {meeting.dialogue.options.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => chooseMeeting(o.id)}
                    disabled={busy}
                    data-testid={`meeting-option-${o.id}`}
                    className="w-full rounded-sm border border-ff-brass/30 bg-ff-bg/50 p-3 text-left text-sm text-ff-parchment transition-colors hover:border-ff-gold hover:bg-ff-bg"
                  >
                    {o.text}
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniMeter({ label, value, good }) {
  return (
    <div>
      <div className="mb-0.5 flex justify-between text-ff-muted"><span>{label}</span><span className="font-mono-ff">{value}</span></div>
      <div className="h-1.5 overflow-hidden rounded-full bg-ff-bg">
        <div className={`h-full ${good ? "bg-ff-success" : "bg-ff-danger"}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function CampBtn({ children, icon: Icon, testid, ...props }) {
  return (
    <button
      data-testid={testid}
      className="flex items-center justify-center gap-1 rounded-sm border border-ff-brass/30 py-2 text-xs font-medium text-ff-parchment transition-colors hover:border-ff-gold hover:text-ff-gold disabled:cursor-not-allowed disabled:opacity-30"
      {...props}
    >
      <Icon size={14} /> {children}
    </button>
  );
}
